import * as aeRoleUtils from './aeRoleUtils';
import * as utils from './common';
import * as csmUtils from './csmRoleUtils';
import * as vpRoleUtils from './vpRoleUtils';
import type {
  InquiryForm,
  JobsSectionField,
} from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import { JobsInquiryCsmQuota, JobsInquiryPosition } from '@/types/graphql';

export const getQuotaAttainmentByTeamField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: utils.getQuotaHitByTeamFieldLabel(inquiry),
      value: utils.getQuotaHitByTeamFieldValue(inquiry),
      attributeName: 'quotaAttainmentByTeamPerPeriod',
      isHidden:
        inquiry.positionMain === JobsInquiryPosition.Csm && inquiry.csmQuotaTypeMain === JobsInquiryCsmQuota.CsmNoQuota,
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.quotaAttainmentByTeamPerPeriod),
  };
};

export const getBaseSalaryField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Base salary',
      value: utils.getBaseSalaryFieldValue(inquiry),
      attributeName: 'baseSalary',
    },
    sensitiveData: true,
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.baseSalary),
  };
};

export const getOteField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'OTE',
      value: utils.getOteFieldValue(inquiry),
      attributeName: 'ote',
    },
    sensitiveData: true,
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.ote),
  };
};

export const getRevenueField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  const year = new Date(inquiry.filledAt).getFullYear() - 1;
  return {
    attrs: {
      label: `Income in ${year}`,
      value: utils.getIncomeLastYearFieldValue(inquiry),
      attributeName: 'revenue',
    },
    sensitiveData: true,
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.revenue),
  };
};

export const getSalesCycleLengthField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Sales cycle length',
      value: utils.getSalesCycleFieldValue(inquiry),
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.salesCycleLength),
  };
};

export const getRankOnTeamField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Your rank in the team',
      value: utils.getRankOnTeamFieldValue(inquiry),
      attributeName: 'rankOnTeam',
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.rankOnTeam),
  };
};

export const getTeamSizeField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Team size',
      value: inquiry?.teamSize,
      attributeName: 'teamSize',
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.teamSize),
  };
};

export const getSalesMethodologiesField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Methodologies I am trained in',
      value: utils.getSalesMethodologiesFieldValue(inquiry),
    },
    fullWidth: true,
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isMultiselectFieldHasSelectedOption(inquiry.salesMethodologies),
  };
};

export const getAeLeadSourceField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Majority of your leads',
      value: utils.getAeLeadSourcesFeldValue(inquiry),
    },
    fullWidth: true,
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) =>
      utils.isMultiselectFieldHasSelectedOptionOrOtherOptionFilled(
        inquiry.aeLeadSourcesMain as string[],
        inquiry.aeLeadSourceOther,
      ),
  };
};

export const getCsmAccountsNumberField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: '# of Accounts Managed',
      value: csmUtils.getAccountNumberFieldValue(inquiry),
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.csmAccountsNumber),
  };
};

export const getCsmTotalBusinessValueField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Book of Business',
      value: csmUtils.getBookOfBusinessFieldValue(inquiry),
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.csmTotalBusinessValue),
  };
};

export const getAeQuotaTypeField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Quota period',
      value: aeRoleUtils.getAeQuotaTypeFieldValue(inquiry),
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) =>
      utils.isSingleFieldFilled(inquiry.aeQuotaTypeMain) || utils.isSingleFieldFilled(inquiry.aeQuotaTypeOther),
  };
};

export const getVpQuotaTypeField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Quota structure',
      value: vpRoleUtils.getVpQuotaStructureFieldValue(inquiry),
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.vpQuotaType),
  };
};

export const getAeQuotaPerPeriodField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Quota amount',
      value: aeRoleUtils.getAeQuotaFieldValue(inquiry),
      attributeName: 'aeQuotaPerPeriod',
    },
    sensitiveData: true,
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.aeQuotaPerPeriod),
  };
};

export const getQuotaAttainmentPerPeriodField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: '% quota attainment last period',
      value: utils.getQuotaAttainment(inquiry),
      attributeName: 'quotaAttainmentPerPeriod',
      isHidden: aeRoleUtils.getIsAeQuotaAttainmentHidden(inquiry),
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.quotaAttainmentPerPeriod),
  };
};

export const getQuotaAttainmentPerLastYearField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: '% quota attainment last year',
      value: aeRoleUtils.getAeLastYearQuotaAttainment(inquiry),
      attributeName: 'quotaAttainmentPerLastYear',
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.quotaAttainmentPerLastYear),
  };
};

export const getDealSizeField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Average deal size',
      value: utils.getDealSizeFieldValue(inquiry),
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.dealSize),
  };
};

export const getVpDepartmentSizeField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Size of department led',
      value: vpRoleUtils.getVpDepartmentSizeFieldValue(inquiry),
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isSingleFieldFilled(inquiry.vpDepartmentSize),
  };
};

export const getVpManagedRolesField = ({ inquiry }: { inquiry: InquiryForm }): JobsSectionField => {
  return {
    attrs: {
      label: 'Managed roles',
      value: vpRoleUtils.getManagedRolesFieldValue(inquiry),
    },
    component: 'PortfolioJobsInquirySectionField',
    getIsFulfilled: ({ inquiry }) => utils.isMultiselectFieldHasSelectedOption(inquiry.vpManagedRoles),
  };
};
